<template>
  <v-container grid-list-xs fluid class="pb-0">
    <v-row row wrap>
      <v-col v-if="loadingUsers" class="standard col-12 pa-0 ma-0">
        <Loader size="small" text="Loading colleagues"></Loader>
      </v-col>
      <v-col
        v-if="!loadingUsers && !hasGroupMembers"
        class="col-10 pa-0 ma-0 text-center"
      >
        No colleagues found
        <span v-if="accessGroupAllowed">with specific group access</span>
      </v-col>
      <v-col v-if="hasGroupMembers" :class="widthClassName" class="pa-0 ma-0">
        <v-autocomplete
          v-model="selectedMembers"
          :items="userGroupMembers"
          :filter="customFilter"
          :error-messages="showError"
          small-chips
          multiple
          deletable-chips
          clearable
          item-text="email"
          label="Select colleagues to share with"
          class="pt-0 mt-0"
          :return-object="returnObject"
          @change="toggleUserSelection"
        ></v-autocomplete>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashSortBy from "lodash/sortBy";
import lodashSortedUniq from "lodash/sortedUniq";

import { mapGetters } from "vuex";
import { GET_GROUP_MEMBERS } from "@/store/actions.type";
import { MODULE_PREFIX_USER } from "@/store";
import { getUserProfile } from "@/common/storage";
import {
  hasEssentialsAccess,
  memberHasAccessGroupByName,
} from "@/services/user/user.service";

const EMITTED_ACTIONS = {
  INPUT: "input",
};

export default {
  name: "UserShareListEditor",
  props: {
    value: {
      type: Array,
      required: true,
      default() {
        return [];
      },
    },
    widthClassName: {
      type: String,
      default: "col-6",
    },
    init: {
      type: String,
    },
    allowEmpty: {
      type: Boolean,
      default: false,
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    accessGroupAllowed: {
      type: String,
    },
    enforceEssentials: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Loader: () => import("@/components/common/Loader"),
  },
  async mounted() {
    this.currentUser = getUserProfile();
    await this.loadUserGroupMembers();
  },
  data() {
    return {
      currentUser: null,
      userGroupMembers: [],
      selectedMembers: this.cleanValues(),
      loadingUsers: true,
      currentUserHasEssentials: hasEssentialsAccess(),
    };
  },
  computed: {
    ...mapGetters({
      groupMembers: MODULE_PREFIX_USER + "groupMembers",
    }),
    hasGroupMembers() {
      return this.userGroupMembers.length > 0;
    },
    showError() {
      if (this.selectedMembers && this.selectedMembers.length > 0) {
        return null;
      }
      if (this.allowEmpty) {
        return null;
      }
      return "At least one colleague must be selected";
    },
    valueCleaned() {
      return this.cleanValues();
    },
  },
  methods: {
    async loadUserGroupMembers() {
      this.loadingUsers = true;
      await this.$store.dispatch(MODULE_PREFIX_USER + GET_GROUP_MEMBERS);
      this.loadingUsers = false;
      if (this.groupMembers.length <= 0) {
        return;
      }

      const SORTED = lodashSortBy(this.groupMembers, "email");
      const SORTED_UNIQUE = lodashSortedUniq(SORTED, "email");
      let mapped = [];
      SORTED_UNIQUE.forEach((member) => {
        let essentialsCheck = true;
        if (this.enforceEssentials) {
          const memberHasEssentialsAccess = memberHasAccessGroupByName(
            member,
            "Seerist_Essentials_Access"
          );
          if (!this.currentUserHasEssentials && memberHasEssentialsAccess) {
            essentialsCheck = false;
          }
        }
        if (
          essentialsCheck &&
          member.email.toLowerCase() !== this.currentUser.email.toLowerCase() &&
          (!this.accessGroupAllowed ||
            (this.accessGroupAllowed && member.isAdmin) ||
            member.accessGroups.includes(this.accessGroupAllowed))
        ) {
          mapped.push({
            id: member.id,
            email: member.email.toLowerCase(),
            selected: this.valueCleaned.indexOf(member.email) > -1,
          });
        }
      });
      this.userGroupMembers = mapped;
    },
    customFilter(item, queryText, itemText) {
      const SEARCH = queryText.toLowerCase();
      const TEXT = item.email.toLowerCase();
      return TEXT.indexOf(SEARCH) > -1;
    },
    toggleUserSelection() {
      this.$emit(EMITTED_ACTIONS.INPUT, this.selectedMembers);
    },
    cleanValues() {
      if (!this.returnObject) {
        if (this.value && Array.isArray(this.value) && this.value.length) {
          return this.value.map((v) => {
            return v.toLowerCase();
          });
        }
        return [];
      } else {
        return this.value;
      }
    },
  },
  watch: {
    init: {
      handler() {
        if (this.init) {
          this.selectedMembers =
            this.valueCleaned && this.valueCleaned.length
              ? this.valueCleaned
              : [];
        }
      },
    },
  },
};
</script>
<style scoped>
.standard {
  font-size: 24px;
}
</style>
